import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type { AssistantTurnRow, TurnsPage } from "./types";

const client = generateClient<Schema>();

/** Rows per round trip. */
const PAGE_SIZE = 200;

/**
 * The most rows one load will read.
 *
 * The table grows with every turn the assistant takes, and this page only ever
 * needs the shape of the last few weeks, not a census. A ceiling keeps the
 * cost of opening it flat as the table grows; when it bites, the page says so
 * rather than quietly under-reporting.
 */
const MAX_ROWS = 2000;

/** Guards against an unbounded loop if a page ever comes back empty with a token. */
const MAX_PAGES = Math.ceil(MAX_ROWS / PAGE_SIZE) + 2;

/**
 * Only what the page renders. `answer` and `toolCalls` are deliberately not
 * fetched: nothing here displays them, and a merchant's question is enough
 * sensitive data to be carrying over the wire.
 *
 * `groundFailure` is fetched, having previously been excluded for the same
 * reason. It records that every draft attempt failed, and why — which is what
 * separates a question nothing in the reference covered from one that failed
 * on the way out. Not proof an answer existed: `model_unavailable:*` means
 * the model was never reached at all. It carries no merchant data; the values
 * are reason names like `unresolved_ref`.
 */
const SELECTION = [
  "id",
  "businessId",
  "question",
  "disposition",
  "created",
  "groundFailure",
] as const;

interface ListByDispositionKey {
  disposition: string;
  created?: { ge?: string; le?: string };
}

interface ListByDispositionOpts {
  sortDirection: "ASC" | "DESC";
  limit: number;
  nextToken?: string;
  selectionSet: readonly string[];
}

interface ListByDispositionResult {
  data: AssistantTurnRow[];
  nextToken?: string | null;
  errors?: { message: string }[];
}

/**
 * Amplify generates `listBy*` from a secondary index at runtime but does not
 * surface it on the generated types, so it is reached through a narrowly
 * scoped cast — the same shape the events repository uses.
 */
function turnsByDisposition(
  key: ListByDispositionKey,
  opts: ListByDispositionOpts,
): Promise<ListByDispositionResult> {
  const model = client.models.AssistantTurn as unknown as {
    listAssistantTurnByDispositionAndCreated: (
      key: ListByDispositionKey,
      opts: ListByDispositionOpts,
    ) => Promise<ListByDispositionResult>;
  };
  return model.listAssistantTurnByDispositionAndCreated(key, opts);
}

export interface ListTurnsOpts {
  disposition: string;
  /** How far back to read, in days. */
  windowDays: number;
}

export interface AssistantRepository {
  listTurns(opts: ListTurnsOpts): Promise<TurnsPage>;
}

/**
 * Reads turns of one disposition over the `disposition`/`created` index.
 *
 * The time window is a key condition rather than a filter, so DynamoDB stops
 * at the edge of the window instead of reading every turn ever written and
 * discarding most of them.
 */
export class DataAssistantRepository implements AssistantRepository {
  async listTurns({
    disposition,
    windowDays,
  }: ListTurnsOpts): Promise<TurnsPage> {
    const since = new Date(
      Date.now() - windowDays * 24 * 60 * 60 * 1000,
    ).toISOString();

    const rows: AssistantTurnRow[] = [];
    let nextToken: string | null | undefined;
    let pages = 0;

    do {
      const result = await turnsByDisposition(
        { disposition, created: { ge: since } },
        {
          sortDirection: "DESC",
          limit: PAGE_SIZE,
          nextToken: nextToken ?? undefined,
          selectionSet: SELECTION,
        },
      );
      if (result.errors?.length) throw new Error(result.errors[0].message);

      rows.push(...(result.data ?? []));
      nextToken = result.nextToken;
      pages += 1;
    } while (nextToken && rows.length < MAX_ROWS && pages < MAX_PAGES);

    return {
      rows: rows.slice(0, MAX_ROWS),
      truncated: Boolean(nextToken) || rows.length > MAX_ROWS,
    };
  }
}

export { MAX_ROWS, PAGE_SIZE };
